import { useState } from 'react';
import { Users, Plus, Search } from 'lucide-react';
import { trpc } from '../lib/trpc.ts';
import { EmptyState } from '../components/EmptyState.tsx';
import { Badge } from '../components/Badge.tsx';
import { Modal } from '../components/Modal.tsx';
import { Reveal } from '../components/motion.tsx';
import { useToast } from '../components/Toast.tsx';
import './Crud.css';

export function Cardholders() {
  const [search, setSearch] = useState('');
  const list = trpc.cardholder.list.useQuery({ search: search || undefined, limit: 200 });
  const utils = trpc.useUtils();
  const toast = useToast();
  const upsert = trpc.cardholder.upsert.useMutation({
    onSuccess: () => utils.cardholder.list.invalidate(),
  });
  const [showForm, setShowForm] = useState(false);

  const count = list.data?.length ?? 0;

  return (
    <div className="stack">
      <Reveal className="crud-head">
        <span className="section-eyebrow">Identity · Credentials</span>
        <h1 className="crud-title">
          <span className="accent">Cardholders</span>
        </h1>
        <span className="crud-sub">
          The people who carry RFID cards or PIN codes. Assign access levels to grant entry.
        </span>
      </Reveal>

      <Reveal delay={0.05} className="card tight">
        <div className="row">
          <Search size={14} className="muted" />
          <input
            placeholder="Search by name or email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <span className="spacer" />
          {!list.isLoading && (
            <span className="crud-count">
              <strong>{String(count).padStart(2, '0')}</strong> records
            </span>
          )}
          <button onClick={() => setShowForm(true)}>
            <Plus size={14} />
            New cardholder
          </button>
        </div>
      </Reveal>

      <Reveal delay={0.1} className="card">
        {list.isLoading ? (
          <div className="stack">
            {[1, 2, 3].map((i) => (
              <div key={i} className="skel" style={{ height: 38 }} />
            ))}
          </div>
        ) : !list.data || list.data.length === 0 ? (
          <EmptyState
            icon={Users}
            title="No cardholders"
            body="Cardholders are the people who carry RFID cards or PIN codes."
            action={
              <button onClick={() => setShowForm(true)}>
                <Plus size={14} />
                Add the first one
              </button>
            }
          />
        ) : (
          <table className="table crud-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Status</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {list.data.map((c) => (
                <tr key={c.id}>
                  <td style={{ fontWeight: 500 }}>
                    {c.firstName} {c.lastName}
                  </td>
                  <td className="dim">{c.email ?? '—'}</td>
                  <td>
                    {c.isDisabled ? (
                      <Badge tone="warn">disabled</Badge>
                    ) : (
                      <Badge tone="ok">active</Badge>
                    )}
                  </td>
                  <td className="dim mono" style={{ fontSize: 12 }}>
                    {new Date(c.createdAt).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Reveal>

      <Modal
        open={showForm}
        onClose={() => setShowForm(false)}
        title="New cardholder"
        dismissable={!upsert.isPending}
        maxWidth={480}
      >
        <p className="dim" style={{ marginTop: 0 }}>
          Phase 1 stub — extend with email, schedule, access levels.
        </p>
        <form
          id="cardholder-form"
          onSubmit={async (e) => {
            e.preventDefault();
            const fd = new FormData(e.currentTarget);
            const firstName = String(fd.get('firstName') ?? '');
            const lastName = String(fd.get('lastName') ?? '');
            await upsert.mutateAsync({
              firstName,
              lastName,
              email: (fd.get('email') as string) || null,
            });
            toast.success('Cardholder added', `${firstName} ${lastName}`.trim());
            setShowForm(false);
          }}
          className="stack"
        >
          <input name="firstName" placeholder="First name" required />
          <input name="lastName" placeholder="Last name" required />
          <input name="email" type="email" placeholder="Email (optional)" />
          <div className="dialog-actions">
            <button type="button" className="ghost" onClick={() => setShowForm(false)}>
              Cancel
            </button>
            <button type="submit" disabled={upsert.isPending}>
              {upsert.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
